import { Select } from "antd";
import dayjs from 'dayjs';
import i18next from "i18next";

const SHA256 = require("crypto-js/sha256");


/**
 * Creates an ISO standard date string.
 * @param {Date} date The date to be converted to a string.
 */
export const dateToISOString = (date) => {
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? '+' : '-';
  const pad = (num) => {
    const norm = Math.floor(Math.abs(num));
    return (norm < 10 ? '0' : '') + norm;
  };

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${dif}${pad(tzo / 60)}:${pad(tzo % 60)}`;
}

/**
 * Returns javascript date from X2O date string formatted according to the PatternTranslationKey.DATE pattern (translated)
 */
export const getDateFromX2OFormattedDate = (dateString) => {
  let translatedX2ODateOnlyFormat = i18next.t('pattern.date_only').toUpperCase();  //uppercase for dates as dateJS uses uppercase codes for dates (not time if copying this function)
  return dayjs(dateString, translatedX2ODateOnlyFormat).toDate();
};

/**
 * Returns max allowed Date in the future.
 */
export const getMaxPossibleDate = () => {
  return new Date(8640000000000000);
};

/**
 * Returns milliseconds value in time format (HH:MM:SS).
 * (does not make sense for periods longer than 24h)
 */
export const getTimeFromMilliseconds = (milliseconds) => {
  let sec = Math.floor(milliseconds / 1000);
  let hrs = Math.floor(sec / 3600);
  sec -= hrs * 3600;
  let min = Math.floor(sec / 60);
  sec -= min * 60;
  sec = '' + sec;
  sec = ('00' + sec).substring(sec.length);
  min = '' + min;
  min = ('00' + min).substring(min.length);
  hrs = '' + hrs;
  if (hrs.length < 3) {
    hrs = ('00' + hrs).substring(hrs.length);
  }
  return hrs + ":" + min + ":" + sec;
};

/**
 * Get random integer between two passed in values
 */
export const getRandomInt = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Returns boolean for passed in simple Y/N string
 * @param paramYorN 'Y' or 'N' (null will be false)
 */
export const isSwitchValueOn = (paramYorN) => {
  if (paramYorN == null) {
    return false;
  } else {
    return paramYorN.toString().toUpperCase() === 'Y';
  }
}

/**
 *  Store value in session storage (this should not include sensitive information, consider cookie for that if needed)
 *  @param key unique identifier
 *  @param value String or Javascript Object (JSON compatible), if null is passed in then the value will not be stored or will be removed
 */
export const storeSessionData = (key, value) => {
  if (value == null) { //do not store and remove if found
    if (sessionStorage.getItem(key) != null) {
      sessionStorage.removeItem(key);
    }
  } else {
    if (typeof value === 'object') {
      sessionStorage.setItem(key, JSON.stringify(value));
    } else {
      sessionStorage.setItem(key, value);
    }
  }
}

export const clearSessionData = () => {
  sessionStorage.clear();
}

/**
 *  Retrieve value from session storage (will return null if not found)
 *  @param key unique identifier
 */
export const getSessionData = (key) => {
  const value = sessionStorage.getItem(key);
  if (value == null) {
    return null;
  } else {
    try {
      const tryObject = JSON.parse(value);
      if (tryObject && typeof tryObject === "object") {
        return tryObject;
      }
    } catch (ignore) {}
    return value;
  }
}

/**
 *  Store value in local storage 
 *  @param key unique identifier
 *  @param value String or Javascript Object (JSON compatible), if null is passed in then the value will not be stored or will be removed
 */
export const storeLocalData = (key, value) => {
  if (value == null) { //do not store and remove if found
    if (localStorage.getItem(key) != null) {
      localStorage.removeItem(key);
    }
  } else {
    if (typeof value === 'object') {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      localStorage.setItem(key, value);
    }
  }
}

/**
 *  Retrieve value from local storage (will return null if not found)
 *  @param key unique identifier
 */
export const getLocalData = (key) => {
  const value = localStorage.getItem(key);
  if (value == null) {
    return null;
  } else {
    try {
      const tryObject = JSON.parse(value);
      if (tryObject && typeof tryObject === "object") {
        return tryObject;
      }
    } catch (ignore) {}
    return value;
  }
}

/**
 * Securely encrypt text/string value USING SHA-256 encryption
 * @param textValue
 * @returns {*}
 */
export const encryptTextValue = (textValue) => {
  return SHA256(textValue).toString();
}

export const subParams = (text, ...params) => {
  var newText = text;
  var index = 0;

  for (var param of params)
    {
      newText = newText.replaceAll('{{' + index + '}}', param);
      newText = newText.replaceAll('{' + index + '}', param);
      index++;
    }

  return newText;
}

export const createSelectOptions = (keyDescs) => {
  if (keyDescs && keyDescs.length > 0)
  {
    return keyDescs.map(keyDesc => <Select.Option key={keyDesc.key} value={keyDesc.key}>{keyDesc.description}</Select.Option>);
  }
  
  return [];
} 

export function ValidatePhoneNumber (value, t) {

  if (!value) {
    return Promise.reject();
  }

  if (value.length < 2 || value.length > 15)
  {
      return Promise.reject(t('error.invalid_phone', {0: t('field.phone')}));
  }

  const firstTest = new RegExp("/*[+0-9]+");
  var isValid = firstTest.test(value.slice(0,1));

  if (isValid)
  {
      const secondTest = new RegExp("^/*[ 0-9]+$");
      isValid = secondTest.test(value.substring(1));
  }

  if (!isValid)
  {
      return Promise.reject(t('error.invalid_phone', {0: t('field.phone')}));
  }

  return Promise.resolve();
}

export function formatAddress(address, separator = ", ")
{
  var result = address.line1;
  if(address.line2)
  {
      result = result + separator + address.line2;
  }
  if(address.line3)
  {
      result = result + separator + address.line3;
  }
  result = result + separator + address.townOrCity;
  if(address.postcode)
  {
      result = result + separator + address.postcode;
  }
  return result;
}

let timeoutId = {};
export const debounce = (callback, wait, callId) => {
  return (...args) => {
  window.clearTimeout(timeoutId[callId]);
    timeoutId[callId] = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
}

export const convertToBase64EncodedFile = (file) => {
  return {
    ...file,
    filename: file.name,
    contentType: file.type,
    contents: file.url?.slice(file.url?.indexOf(',') + 1),
    
  }
}

export const getTagColour = (status) =>
  {
      // Initial
      if (status === "REPAIRER_CONFIRMED" || status === "REPAIR_ESTIMATE_BOOKED" || status === "REPAIRER_ALLOCATED" || status === "REPAIR_BOOKING_CONFIRMED" ||
          status === "REPAIR_BOOKED" || status === "CREATED")
      {
          return "geekblue";
      }

      // In Progress
      if (status === "REPAIR_IN_PROGRESS" || status === "AUTHORISED_AND_ARRIVED" || status === "IN_PROGRESS")
      {
          return "chartreuse";
      }

      // Positive
      if (status === "REPAIR_ESTIMATE_ACCEPTED" || status === "REPAIR_AUTHORISED" || status === "REPAIR_AWAITING_ESTIMATE" || status === "REPAIR_ESTIMATE_CONFIRMED" || 
          status === "REPAIR_ESTIMATED")
      {
          return "lime";
      }

      // Positive - dark green
      if (status === "REPAIR_COMPLETE" || status === "SUPPLIER_INVOICED" || status === "COMPLETE")
      {
          return "green";
      }

      // Negative
      if (status === "REPAIRER_DECLINED" || status === "REPAIR_CANCELLED" || status === "REPAIR_ESTIMATE_REJECTED" || status === "DAMAGE_TOTAL_LOSS" || 
          status === "REPAIR_SUSPENDED" || status === "REPAIR_ON_HOLD" || status === "CANCELLED")
      {
          return "darkgrey";
      }
  }

  export const getMyVehicleTitleKey = () =>
  {
      const vehicles = getSessionData('allVehicles');
      if(!vehicles || vehicles.length <= 1)
      {
          return 'title.my_vehicle';
      }
      else
      {
          return 'title.my_vehicles';
      }
  }

  export const escapeCodeForApiCall = (code) => {
    return encodeURIComponent(code);
  }